import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Grid
} from '@mui/material';
import {
  X,
  BookOpen,
  Search,
  ListMusic,
  Film,
  Sparkles,
  Volume2,
  QrCode,
  Compass
} from 'lucide-react';

export default function UsageTutorialDialog({ open, onClose, onStartTour }) {
  const guideFeatures = [
    {
      icon: <Search size={20} color="#00f0ff" />,
      title: '1. Búsqueda Inteligente',
      desc: 'Escribe el nombre de la canción o cantante en la barra superior. Con un clic puedes reproducirla al instante o mandarla a la cola de espera de la playlist.'
    },
    {
      icon: <ListMusic size={20} color="#a855f7" />,
      title: '2. Cola de Espera Drag & Drop',
      desc: 'Organiza la fila de cantantes fácilmente. Arrastra las canciones hacia arriba o abajo para cambiar turnos al vuelo sin interrumpir el show.'
    },
    {
      icon: <Film size={20} color="#ff007f" />,
      title: '3. Intros y Transiciones',
      desc: 'Elige cortinas de bienvenida y animaciones que se reproducen automáticamente entre tema y tema, creando un clima de show profesional sin baches de silencio.'
    },
    {
      icon: <Volume2 size={20} color="#00f078" />,
      title: '4. Efectos de Sonido en Vivo',
      desc: '¡Dale emoción a cada participante! Presiona botones de aplausos, risas, ovaciones o abucheos. Puedes calibrar su volumen independientemente de 0% a 200%.'
    },
    {
      icon: <QrCode size={20} color="#ffb703" />,
      title: '5. Pedidos desde el Celular con QR',
      desc: 'Tus invitados pueden escanear el código QR en pantalla para ver el catálogo y enviar sus pedidos de canciones directamente desde sus teléfonos sin levantarse de la mesa.'
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#0f081d !important',
          backgroundColor: '#0f081d !important',
          backdropFilter: 'none !important',
          border: '1px solid rgba(168, 85, 247, 0.35)',
          borderRadius: 3,
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.98), 0 0 40px rgba(168, 85, 247, 0.25)',
          color: '#fff',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          pb: 2,
          pt: 2.5,
          px: 3,
          mb: 2,
          background: '#150a26'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(0, 240, 255, 0.2))',
              border: '1px solid rgba(168, 85, 247, 0.4)'
            }}
          >
            <BookOpen size={22} color="#c084fc" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '0.5px', color: '#fff', fontSize: '1.2rem' }}>
              Tutorial de Uso y Funciones
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(192, 132, 252, 0.9)', fontWeight: 500 }}>
              Guía rápida para dominar todas las herramientas de Garaoke
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.6)',
            '&:hover': { color: '#ff0055', transform: 'rotate(90deg)', transition: 'all 0.25s ease' }
          }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      {/* Content with customized scrollbar */}
      <DialogContent
        sx={{
          py: 3,
          px: 3,
          maxHeight: '65vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(168, 85, 247, 0.3)',
            borderRadius: '10px',
            '&:hover': {
              background: 'rgba(168, 85, 247, 0.6)'
            }
          }
        }}
      >
        <Grid container spacing={2}>
          {guideFeatures.map((item, idx) => (
            <Grid size={{ xs: 12 }} key={idx}>
              <Box
                sx={{
                  p: 2.2,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.07)',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(0, 240, 255, 0.25)',
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.8 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.98rem' }}>
                    {item.title}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.72)', lineHeight: 1.6, pl: 5.2 }}>
                  {item.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Live Tour Callout */}
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1) 0%, rgba(168, 85, 247, 0.15) 100%)',
            border: '1px solid rgba(0, 240, 255, 0.3)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Sparkles size={26} color="#00f0ff" />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff' }}>
                ¿Prefieres un recorrido interactivo paso a paso?
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Podemos guiarte directamente en tu pantalla señalando cada botón y control.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={() => {
              onClose();
              if (onStartTour) {
                setTimeout(onStartTour, 200);
              }
            }}
            startIcon={<Compass size={16} />}
            sx={{
              background: 'linear-gradient(135deg, #00f0ff 0%, #7000ff 100%)',
              color: '#0a0d18',
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '0.85rem',
              boxShadow: '0 0 15px rgba(0, 240, 255, 0.3)',
              flexShrink: 0,
              '&:hover': {
                background: 'linear-gradient(135deg, #33f3ff 0%, #851aff 100%)',
                boxShadow: '0 0 20px rgba(0, 240, 255, 0.6)'
              }
            }}
          >
            Iniciar Paseo Interactivo
          </Button>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          py: 1.8,
          px: 3,
          background: 'rgba(10, 13, 22, 0.6)'
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { color: '#fff', background: 'rgba(255, 255, 255, 0.06)' }
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
